// Hebrew text folding for matching.
//
// Every site needs the same fold before comparing text: unify final letters so
// a query for ירושלם also hits ירושלים, and drop the editorial marks that
// manuscripts and printed editions are full of. It was written independently
// in each of them — twice inside br-viewer alone, plus its Python index
// builder and dev server — and the copies had already drifted: one dropped a
// non-Hebrew character, the others replaced it with a space, so an abbreviation
// like אמ'ר folded to either אמר or אמ ר depending on which search you hit.
// 8.1% of Vat 30's lines normalised differently between the two.
//
// The space-substituting form is the correct one and the one implemented here.
// Deleting the mark instead JOINS the letters around it, which invents words
// that are not in the text: ויבן.ייי becomes ויבנייי, matching nothing, and
// worse, אל'ים becomes אלימ, a real word the manuscript does not have there.
// A separator is a word boundary; treating it as one keeps every token real.

const FINALS = {'ך': 'כ', 'ם': 'מ', 'ן': 'נ', 'ף': 'פ', 'ץ': 'צ'};

/** Fold one string for matching: finals unified, non-letters → space. */
export function fold(s) {
    return String(s == null ? '' : s)
        .replace(/[ךםןףץ]/g, c => FINALS[c])
        .replace(/[^א-ת ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** The letters of a query, finals folded, with separators removed entirely.
 *  Used to re-find a match on the DISPLAY text, where marks sit between the
 *  letters the fold has already dropped. */
export function letters(s) {
    return fold(s).replace(/ /g, '');
}

// Both spellings of a foldable letter, for matching against unfolded text.
const EITHER = {'כ': '[כך]', 'מ': '[מם]', 'נ': '[נן]', 'פ': '[פף]', 'צ': '[צץ]'};

/** A regex matching `query`'s letters in raw text, tolerating any run of
 *  non-letters between them — which is how the display text differs from the
 *  folded text the index matched on. */
export function displayRegex(query, flags) {
    const ls = letters(query);
    if (!ls) return null;
    const body = ls.split('').map(c => EITHER[c] || c).join('[^א-ת]*');
    return new RegExp(body, flags);
}

/** Wrap the first occurrence of `query` in `text` with `open`/`close`.
 *  `escape` renders the untouched parts (pass an HTML escaper when building
 *  markup). Returns the escaped text unchanged when there is no match. */
export function markMatch(text, query, {open = '<mark>', close = '</mark>', escape = (x => x)} = {}) {
    const t = String(text == null ? '' : text);
    const re = displayRegex(query);
    const m = re && re.exec(t);
    if (!m) return escape(t);
    return escape(t.slice(0, m.index)) + open + escape(m[0]) + close
         + escape(t.slice(m.index + m[0].length));
}

export const finals = FINALS;

// Hebrew numerals, for folio and section labels (דף ב, פרק טו). 15 and 16 are
// written טו/טז, never the divine-name spellings.
const NUMERALS = [[400, 'ת'], [300, 'ש'], [200, 'ר'], [100, 'ק'], [90, 'צ'], [80, 'פ'], [70, 'ע'],
    [60, 'ס'], [50, 'נ'], [40, 'מ'], [30, 'ל'], [20, 'כ'], [10, 'י'], [9, 'ט'], [8, 'ח'], [7, 'ז'],
    [6, 'ו'], [5, 'ה'], [4, 'ד'], [3, 'ג'], [2, 'ב'], [1, 'א']];
const NUMERAL_VALUE = Object.fromEntries(NUMERALS.map(([v, c]) => [c, v]));
Object.assign(NUMERAL_VALUE, {'ך': 20, 'ם': 40, 'ן': 50, 'ף': 80, 'ץ': 90});

/** 1 → א, 15 → טו, 116 → קטז. Non-positive input comes back as digits. */
export function numeral(n) {
    n = parseInt(n, 10);
    if (!(n > 0)) return String(n);
    let s = '';
    for (const [v, c] of NUMERALS) while (n >= v) { s += c; n -= v; }
    return s.replace(/יה$/, 'טו').replace(/יו$/, 'טז');
}

/** קטז → 116; geresh/gershayim and quotes are ignored. NaN when not a numeral. */
export function numeralValue(s) {
    const t = String(s == null ? '' : s).replace(/['"׳״\s]/g, '');
    if (!t) return NaN;
    let n = 0;
    for (const c of t) { if (!(c in NUMERAL_VALUE)) return NaN; n += NUMERAL_VALUE[c]; }
    return n;
}
